import { useState, useEffect } from 'react';
import { Save, Activity, Network, Hash, Zap, FolderPlus } from 'lucide-react';
import Modal from '../common/Modal';
import type { ApplicationDefinition, ApplicationCreateData, ApplicationGroup } from '../../services';
import { usePermissions } from '../../hooks/usePermissions';

interface ApplicationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: ApplicationCreateData & { groupIds?: string[] }) => void;
    application?: ApplicationDefinition;
    mode: 'add' | 'edit';
    appGroups?: ApplicationGroup[];
}

const ApplicationModal = ({ isOpen, onClose, onSave, application, mode, appGroups = [] }: ApplicationModalProps) => {
    const { canCreate, canUpdate } = usePermissions();
    const [formData, setFormData] = useState<ApplicationCreateData & { groupIds: string[] }>({
        name: '',
        description: '',
        category: undefined,
        protocol: undefined,
        destination_ports: '',
        source_ports: '',
        dscp_values: '',
        ip_protocol: undefined,
        business_criticality: undefined,
        bandwidth_requirement: undefined,
        latency_requirement: undefined,
        jitter_requirement: undefined,
        packet_loss_tolerance: '',
        enabled: 1,
        groupIds: [],
    });

    useEffect(() => {
        if (isOpen) {
            if (application) {
                setFormData({
                    name: application.name || '',
                    description: application.description || '',
                    category: application.category,
                    protocol: application.protocol,
                    destination_ports: application.destination_ports || '',
                    source_ports: application.source_ports || '',
                    dscp_values: application.dscp_values || '',
                    ip_protocol: application.ip_protocol,
                    business_criticality: application.business_criticality,
                    bandwidth_requirement: application.bandwidth_requirement,
                    latency_requirement: application.latency_requirement,
                    jitter_requirement: application.jitter_requirement,
                    packet_loss_tolerance: application.packet_loss_tolerance || '',
                    enabled: application.enabled,
                    groupIds: application.groups ? application.groups.map(g => g.id) : [],
                });
            } else {
                setFormData({
                    name: '',
                    description: '',
                    category: undefined,
                    protocol: undefined,
                    destination_ports: '',
                    source_ports: '',
                    dscp_values: '',
                    ip_protocol: undefined,
                    business_criticality: undefined,
                    bandwidth_requirement: undefined,
                    latency_requirement: undefined,
                    jitter_requirement: undefined,
                    packet_loss_tolerance: '',
                    enabled: 1,
                    groupIds: [],
                });
            }
        }
    }, [isOpen, application]);

    const handleChange = (field: keyof ApplicationCreateData, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleGroupToggle = (groupId: string) => {
        setFormData(prev => {
            const current = prev.groupIds || [];
            if (current.includes(groupId)) {
                return { ...prev, groupIds: current.filter(id => id !== groupId) };
            } else {
                return { ...prev, groupIds: [...current, groupId] };
            }
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    const footer = (
        <div className="flex items-center justify-end space-x-3">
            <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
            </button>
            {(mode === 'add' ? canCreate : canUpdate) && (
                <button form="application-form" type="submit" className="btn-primary inline-flex items-center space-x-2">
                    <Save className="w-4 h-4" />
                    <span>{mode === 'add' ? 'Add Application' : 'Save Changes'}</span>
                </button>
            )}
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={mode === 'add' ? 'Add Application' : `Edit Application: ${formData.name}`}
            size="xl"
            footer={footer}
        >
            <form id="application-form" onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Application Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Activity className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="e.g., MS Teams, Zoom"
                                className="input-base w-full pl-10"
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Category</label>
                        <select
                            value={formData.category || ''}
                            onChange={(e) => handleChange('category', e.target.value || undefined)}
                            className="input-base w-full"
                        >
                            <option value="">Select category...</option>
                            <option value="collaboration">Collaboration</option>
                            <option value="business">Business</option>
                            <option value="video">Video</option>
                            <option value="voip">VoIP</option>
                            <option value="database">Database</option>
                            <option value="web">Web</option>
                            <option value="email">Email</option>
                            <option value="file_transfer">File Transfer</option>
                            <option value="network">Network</option>
                            <option value="remote">Remote Access</option>
                            <option value="vpn">VPN</option>
                            <option value="authentication">Authentication</option>
                            <option value="custom">Custom</option>
                        </select>
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        placeholder="What is this application for?"
                        className="input-base w-full resize-none"
                        rows={2}
                    />
                </div>

                {/* Application Groups */}
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 flex items-center">
                        <FolderPlus className="w-4 h-4 mr-2 text-primary-500" />
                        Application Groups <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="border border-gray-200 rounded-md p-3 max-h-40 overflow-y-auto bg-gray-50">
                        {appGroups.length === 0 ? (
                            <p className="text-sm text-gray-500 italic">No application groups available.</p>
                        ) : (
                            <div className="space-y-2">
                                {appGroups.map(group => (
                                    <label key={group.id} className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.groupIds.includes(group.id)}
                                            onChange={() => handleGroupToggle(group.id)}
                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-sm text-gray-700">{group.name}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                    {formData.groupIds.length === 0 && (
                        <p className="text-xs text-red-500 mt-1">At least one group must be selected.</p>
                    )}
                </div>


                {/* Traffic Classification */}
                <div className="border-t border-gray-100 pt-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                        <Network className="w-4 h-4 mr-2 text-primary-500" />
                        Traffic Classification
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Protocol</label>
                            <select
                                value={formData.protocol || ''}
                                onChange={(e) => handleChange('protocol', e.target.value || undefined)}
                                className="input-base w-full"
                            >
                                <option value="">Any</option>
                                <option value="tcp">TCP</option>
                                <option value="udp">UDP</option>
                                <option value="icmp">ICMP</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">IP Protocol Number</label>
                            <input
                                type="number"
                                value={formData.ip_protocol || ''}
                                onChange={(e) => handleChange('ip_protocol', e.target.value ? parseInt(e.target.value) : undefined)}
                                placeholder="e.g., 6 (TCP), 17 (UDP)"
                                className="input-base w-full"
                                min="0"
                                max="255"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700 flex items-center">
                                <Hash className="w-4 h-4 mr-1 text-gray-400" />
                                Source Ports
                            </label>
                            <input
                                type="text"
                                value={formData.source_ports}
                                onChange={(e) => handleChange('source_ports', e.target.value)}
                                placeholder="e.g., 1024-65535"
                                className="input-base w-full"
                            />
                            <p className="text-xs text-gray-500">Single port (443), list (80,443), or range (5060-5090)</p>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700 flex items-center">
                                <Hash className="w-4 h-4 mr-1 text-gray-400" />
                                Destination Ports
                            </label>
                            <input
                                type="text"
                                value={formData.destination_ports}
                                onChange={(e) => handleChange('destination_ports', e.target.value)}
                                placeholder="e.g., 443, 8080"
                                className="input-base w-full"
                            />
                            <p className="text-xs text-gray-500">Single port (443), list (80,443), or range (5060-5090)</p>
                        </div>

                        <div className="space-y-2 md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">DSCP Values</label>
                            <input
                                type="text"
                                value={formData.dscp_values}
                                onChange={(e) => handleChange('dscp_values', e.target.value)}
                                placeholder="e.g., 46 (EF), 26,34 (AF31,AF41)"
                                className="input-base w-full"
                            />
                            <p className="text-xs text-gray-500">Comma-separated DSCP values for QoS classification</p>
                        </div>
                    </div>
                </div>

                {/* Business Requirements */}
                <div className="border-t border-gray-100 pt-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                        <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                        Business Requirements
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Business Criticality</label>
                            <select
                                value={formData.business_criticality || ''}
                                onChange={(e) => handleChange('business_criticality', e.target.value || undefined)}
                                className="input-base w-full"
                            >
                                <option value="">Select criticality...</option>
                                <option value="critical">Critical</option>
                                <option value="high">High</option>
                                <option value="medium">Medium</option>
                                <option value="low">Low</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Status</label>
                            <select
                                value={formData.enabled}
                                onChange={(e) => handleChange('enabled', parseInt(e.target.value))}
                                className="input-base w-full"
                            >
                                <option value="1">Enabled</option>
                                <option value="0">Disabled</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Bandwidth Requirement (Mbps)</label>
                            <input
                                type="number"
                                value={formData.bandwidth_requirement || ''}
                                onChange={(e) => handleChange('bandwidth_requirement', e.target.value ? parseInt(e.target.value) : undefined)}
                                placeholder="e.g., 100"
                                className="input-base w-full"
                                min="0"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Latency Requirement (ms)</label>
                            <input
                                type="number"
                                value={formData.latency_requirement || ''}
                                onChange={(e) => handleChange('latency_requirement', e.target.value ? parseInt(e.target.value) : undefined)}
                                placeholder="e.g., 50"
                                className="input-base w-full"
                                min="0"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Jitter Requirement (ms)</label>
                            <input
                                type="number"
                                value={formData.jitter_requirement || ''}
                                onChange={(e) => handleChange('jitter_requirement', e.target.value ? parseInt(e.target.value) : undefined)}
                                placeholder="e.g., 10"
                                className="input-base w-full"
                                min="0"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Packet Loss Tolerance (%)</label>
                            <input
                                type="text"
                                value={formData.packet_loss_tolerance}
                                onChange={(e) => handleChange('packet_loss_tolerance', e.target.value)}
                                placeholder="e.g., 0.1"
                                className="input-base w-full"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                    <p className="text-sm text-blue-700">
                        <strong>Note:</strong> Applications are matched based on traffic classification criteria.
                        Business requirements help routing policies determine the optimal path for this traffic.
                    </p>
                </div>
            </form>
        </Modal>
    );
};

export default ApplicationModal;
